import { useCallback, useEffect, useRef, useState } from "react";

// Highchart
import Highcharts from "highcharts";
import HC_more from "highcharts/highcharts-more";

import "assets/styles/components/highcharts.css";

// Config
import { getOptions } from "./BehaviouralBreakdown.config";

// Hooks
import useChartSlice from "store/slice/charts/useChartSlice";

// Data
import { convertData, carouselData, carouselDataTitles } from "./BehaviouralBreakdown.data";

// Types
import { LD12Chart } from "store/slice/charts/chartsSlice.types";
import { ChartStatus, convertedDataBB, TBehaviourLabels } from "./BehaviouralBreakdown.types";
import { IBaseChartProps, IRefObjectForHighchartsReact } from "components/Charts/chart.types";

// Utils
import { flattenDeep, isEmpty, isEqual } from "lodash";
import { getElementBBox } from "utils";

// Create chart elements
import {
    createMasks,
    createWrapperGroups,
    createSubBehaviourBackground,
    createBehaviourBar,
    createExpanderButton,
    createPlotLines,
    createLabels,
    createSubLabels,
    createSummaryDot,
    createSummaryDotLabel,
    createDotLineConnector,
    createSubBehaviourDots,
    createSubDotLineConnector,
    buttonHeight,
    chartVerticalSpace,
    gapBetweenBehaviours,
    legendSpace,
    rowHeight,
    transitionSpeed,
    hubAverageColor,
    createCustomLegend,
    dotRadius,
    legenItemVerticalSpace,
    expandedLegendUtils,
} from "./BehaviouralBreakdown.functions";
import { TCustomiseLabelsOptions } from "../CustomiseLabels/CustomiseLabels.config";

HC_more(Highcharts);

const useBehaviouralBreakdown = ({ width, height, moreOptions, getData }: IBaseChartProps) => {
    const paceCurrentSlide = useRef(0);
    const triadCurrentSlide = useRef(0);

    const chartData = useChartSlice(getData) as LD12Chart;
    const prevChartData = useRef<LD12Chart | undefined>(undefined);

    const chartRef = useRef<IRefObjectForHighchartsReact>(null);
    const dataRef = useRef<convertedDataBB[] | undefined>(undefined);
    const arechartOptionsLoaded = useRef(false);

    const [chartOptions, setChartOptions] = useState<Highcharts.Options | null>(null);

    const [customiseLabels, setCustomiseLabels] = useState<TCustomiseLabelsOptions>({
        "show-names": false,
    });

    const [shouldExpandAll, setShouldExpandAll] = useState(false);
    const [showDefinitions, setShowDefinitions] = useState(false);
    const [filterBy, setFilterBy] = useState<TBehaviourLabels | undefined>();

    const [render, setRender] = useState(false);

    let data = {} as convertedDataBB[];

    const useCustomLegend = Boolean(moreOptions?.useCustomLegend);
    const hideLegend = Boolean(moreOptions?.hideLegend) || useCustomLegend;
    const hideExpandButtons = moreOptions?.hideExpandButtons === undefined ? false : moreOptions?.hideExpandButtons;
    const shouldShowExportingMenu = moreOptions?.exporting === undefined ? true : moreOptions?.exporting;

    const handleCheckExpandAll = (ev: React.ChangeEvent<HTMLInputElement>) => {
        handleExpandAll(ev.target.checked);
        if (!ev.target.checked && moreOptions?.setShowPaceDefinitions) {
            moreOptions.setShowPaceDefinitions(ev.target.checked);
        }
    };

    const handleCheckPaceDefinitions = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = ev.target.checked;

        setShowDefinitions(isChecked);

        chartRef.current?.chart.update({
            exporting: {
                enabled: !isChecked,
            },
        });

        if (!isChecked && moreOptions?.setShowPaceDefinitions) {
            moreOptions.setShowPaceDefinitions(isChecked);
        }
    };

    const handleSetCustomiseLabels = (options: TCustomiseLabelsOptions) => {
        setCustomiseLabels(options);
    };

    const checkExpandedItems = () => {
        if (isEmpty(data) && !isEmpty(dataRef.current)) {
            data = dataRef.current as convertedDataBB[];
        }

        setShouldExpandAll(data.filter((d) => !isEmpty(d.svgs.button.svg)).every((e) => e.expanded));
    };

    const hideDotAndLine = (keyIndex: number, transitionSpeed: number) => {
        const currentEl = data[keyIndex];
        const previousEl = data[keyIndex - 1];
        const nextEl = data[keyIndex + 1];

        if (currentEl.expanded) {
            // Dots
            currentEl.svgs.dots.forEach((dot) => {
                dot.config = {
                    ...dot.config,
                    css: {
                        ...dot.config?.css,
                        fill: "transparent",
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                dot.svg.css(dot.config.css as Highcharts.CSSObject);
            });

            // Dot Label
            currentEl.svgs.dotsLabels.forEach((dotLabel) => {
                dotLabel.config = {
                    ...dotLabel.config,
                    css: {
                        ...dotLabel.config.css,
                        color: "transparent",
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                dotLabel.svg.css(dotLabel.config.css as Highcharts.CSSObject);
            });

            // Lines
            currentEl.svgs?.lines?.length &&
                currentEl.svgs.lines.forEach((line) => {
                    line.config = {
                        ...line.config,
                        css: {
                            ...line.config?.css,
                            "stroke-width": 0,
                            transition: `all ${transitionSpeed / 2}ms`,
                        },
                    };
                    line.svg.css(line.config.css as Highcharts.CSSObject);
                });
            previousEl?.svgs?.lines?.length &&
                previousEl.svgs.lines.forEach((line) => {
                    line.config = {
                        ...line.config,
                        css: {
                            ...line.config?.css,
                            "stroke-width": 0,
                            transition: `all ${transitionSpeed / 2}ms`,
                        },
                    };
                    line.svg.css(line.config.css as Highcharts.CSSObject);
                });
        } else {
            setTimeout(() => {
                // Summary Dots
                currentEl.svgs.dots.forEach((dot, i) => {
                    const isVisible = currentEl.data[i].visible;
                    const isEnabled = currentEl.data[i].enabled;
                    const isExpanded = currentEl.expanded;

                    const isHubAverage = currentEl.data[i].label === "Hub Average";

                    const isNotExpandedAndVisible = !isExpanded && isVisible;

                    const baseColor = isHubAverage ? hubAverageColor : currentEl.data[i].color;
                    const fillColor = isNotExpandedAndVisible ? baseColor : "transparent";

                    dot.config = {
                        ...dot.config,
                        css: {
                            ...dot.config.css,
                            fill: !isEnabled || !isVisible ? "transparent" : fillColor,
                            transition: `all ${transitionSpeed / 2}ms`,
                        },
                    };

                    dot.svg.css(dot.config.css as Highcharts.CSSObject);
                });

                // Summary Dot Label

                currentEl.svgs.dotsLabels.forEach((dotLabel, i) => {
                    dotLabel.config = {
                        ...dotLabel.config,
                        css: {
                            ...dotLabel.config.css,
                            color: "#454852",
                            // opacity: data[keyIndex].isVisible[i] ? 1 : 0,
                            opacity: data[keyIndex].data[i].visible ? 1 : 0,
                            transition: `all ${transitionSpeed / 2}ms`,
                        },
                    };

                    dotLabel.svg.css(dotLabel.config.css as Highcharts.CSSObject);
                });

                // Lines
                if (nextEl && !nextEl.expanded) {
                    currentEl.svgs.lines.forEach((line, i) => {
                        const isVisible = data[keyIndex].data[i].visible;
                        const isEnabled = data[keyIndex].data[i].enabled;

                        line.config = {
                            ...line.config,
                            css: {
                                ...line.config.css,
                                "stroke-width": isEnabled && isVisible ? 1 : 0,
                                transition: `all ${transitionSpeed / 2}ms`,
                            },
                        };

                        line.svg.css(line.config.css as Highcharts.CSSObject);
                    });
                }

                if (previousEl?.svgs?.lines?.length && !previousEl.expanded) {
                    previousEl.svgs.lines.forEach((line, i) => {
                        const isVisible = data[keyIndex].data[i].visible;
                        const isEnabled = data[keyIndex].data[i].enabled;

                        line.config = {
                            ...line.config,
                            css: {
                                ...line.config.css,
                                "stroke-width": isEnabled && isVisible ? 1 : 0,
                                transition: `all ${transitionSpeed / 2}ms`,
                            },
                        };

                        line.svg.css(line.config.css as Highcharts.CSSObject);
                    });
                }
            }, transitionSpeed / 4);
        }
    };

    const handleExpandCollapse = (i: number, chart: Highcharts.Chart, transitionSpeed: number) => {
        data[i].expanded = !data[i].expanded;

        const height = data[i].subData?.data?.length || 1;

        // Mask
        data[i].svgs.mask.config = {
            ...data[i].svgs.mask.config,
            css: {
                ...data[i].svgs.mask.config.css,
                height: `${!data[i].expanded ? rowHeight + buttonHeight : height * rowHeight + buttonHeight}px`,
                transition: `all ${transitionSpeed}ms`,
            },
        };
        data[i].svgs.mask.svg.css(data[i].svgs.mask.config.css || {});

        // Button
        data[i].svgs.button.config = {
            ...data[i].svgs.button.config,
            css: {
                ...data[i].svgs.button.config.css,
                transform: `translate(${chartVerticalSpace}px, ${
                    !data[i].expanded ? `${rowHeight + 2}px` : `${height * rowHeight}px`
                })`,
                transition: `all ${transitionSpeed}ms`,
            },
        };
        data[i].svgs.button.svg.css(data[i].svgs.button.config.css as Highcharts.CSSObject);

        // Button Chevrons
        data[i].svgs.buttonChevrons.forEach((chevron) => {
            const chevronWidth = chevron.svg.element.getBoundingClientRect().width;
            chevron.config = {
                ...chevron.config,
                css: {
                    ...chevron.config?.css,
                    transform: `translate(${
                        // @ts-ignore
                        chevron.svg.element.getCTM().e + (!data[i].expanded ? -chevronWidth - 2 : chevronWidth + 2)
                    }px, ${!data[i].expanded ? `${rowHeight + 1}px` : `${height * rowHeight + 10}px`}) rotate(${
                        !data[i].expanded ? 0 : 180
                    }deg)`,
                    transition: `all ${transitionSpeed}ms`,
                },
            };
            chevron.svg.css(chevron.config.css as Highcharts.CSSObject);
        });

        // Labels
        data[i].svgs.labels?.forEach((label) => {
            label.config = {
                ...label.config,
                css: {
                    ...label.config.css,
                    opacity: !data[i].expanded ? 1 : 0,
                    transition: `all ${transitionSpeed / 2}ms`,
                },
            };
            label.svg.css(label.config.css as Highcharts.CSSObject);
        });

        // Rotated Labels
        data[i].svgs.rotatedLabels?.forEach((label) => {
            label.config = {
                ...label.config,
                css: {
                    ...label.config.css,
                    opacity: !data[i].expanded ? 0 : 1,
                    transition: `all ${transitionSpeed / 2}ms`,
                },
            };
            label.svg.css(label.config.css as Highcharts.CSSObject);
        });

        // Sub-Behaviours Labels
        data[i].svgs.subData?.labels?.forEach((labelGroup) => {
            labelGroup.forEach((label) => {
                label.config = {
                    ...label.config,
                    css: {
                        ...label.config.css,
                        opacity: !data[i].expanded ? 0 : 1,
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                label.svg.css(label.config.css as Highcharts.CSSObject);
            });
        });

        // Sub-Behaviours Line Connectors
        data[i].svgs.subData?.lines?.forEach((arrayOfLines) => {
            arrayOfLines.forEach((line, lineIdx) => {
                const isVisible = data[i].data[lineIdx].visible;
                const isEnabled = data[i].data[lineIdx].enabled;
                const isExpanded = data[i].expanded;

                const isHubAverage = data[i].data[lineIdx].label === "Hub Average";
                const strokeWidth = isVisible && isExpanded ? 1 : 0;

                line.config = {
                    ...line.config,
                    attr: {
                        ...(line.config.attr as Highcharts.SVGAttributes),
                        "stroke-width": !isEnabled ? 0 : strokeWidth,
                        stroke: isHubAverage ? hubAverageColor : data[i].data[lineIdx].color,
                    },
                    css: {
                        ...line.config?.css,
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                line.svg.attr(line.config.attr);
                line.svg.css(line.config.css as Highcharts.CSSObject);
            });
        });

        // Sub-Behaviours Dots
        data[i].svgs.subData?.dots?.forEach((arrayOfDots) => {
            arrayOfDots.forEach((dot, dotIdx) => {
                const isVisible = data[i].data[dotIdx].visible ? 1 : 0;
                const isEnabled = data[i].data[dotIdx].enabled;
                const isExpanded = data[i].expanded;

                const isHubAverage = data[i].data[dotIdx].label === "Hub Average";
                const fillColor = isHubAverage ? hubAverageColor : data[i].data[dotIdx].color;
                const fillOpacity = isVisible && isExpanded ? 1 : 0;

                dot.config = {
                    ...dot.config,
                    css: {
                        ...dot.config.css,
                        fill: !isEnabled ? "transparent" : fillColor,
                        opacity: !isEnabled ? 0 : fillOpacity,
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                dot.svg.css(dot.config.css as Highcharts.CSSObject);
            });
        });

        // Sub-Behaviours Dot Labels
        data[i].svgs.subData?.dotsLabels?.forEach((label, idx) => {
            const isVisible = data[i].data[idx].visible;
            const isEnabled = data[i].data[idx].enabled;
            const isExpanded = data[i].expanded;

            const color = isVisible && isExpanded ? "#454852" : "transparent";

            label.config = {
                ...label.config,
                css: {
                    ...label.config.css,
                    color: isEnabled ? "#454852" : color,
                    transition: `all ${transitionSpeed / 2}ms`,
                },
            };
            label.svg.css(label.config.css as Highcharts.CSSObject);
        });

        hideDotAndLine(i, transitionSpeed);

        if (i < data.length - 1) {
            data.slice(i + 1, data.length).forEach((element) => {
                // @ts-ignore
                const heightToPush = element.svgs.wrapperGroup.svg.element.getCTM().f;

                element.svgs.wrapperGroup.config = {
                    ...element.svgs.wrapperGroup.config,
                    css: {
                        ...element.svgs.wrapperGroup.config.css,
                        transform: `translate(0, ${
                            !data[i].expanded
                                ? `${heightToPush - (data[i].subData?.data?.length - 1 || 1) * rowHeight}px`
                                : `${heightToPush + (data[i].subData?.data?.length - 1 || 1) * rowHeight}px`
                        })`,
                        transition: `all ${transitionSpeed}ms`,
                    },
                };

                element.svgs.wrapperGroup.svg.css(element.svgs.wrapperGroup.config.css || {});
            });
        }

        // Legend
        if (useCustomLegend) {
            const lastWrapper = (data.at(-1) as convertedDataBB).svgs.wrapperGroup.svg;
            const lastWrapperStyles = lastWrapper.attr("style").toString();
            const transform = lastWrapperStyles
                .split(";")
                .find((e) => e.includes("transform"))
                ?.match(/\d+/g)
                ?.map((e) => Number(e)) || [0, 0];

            data[0].svgs.legend.wrapperGroup.config = {
                ...data[0].svgs.legend.wrapperGroup.config,
                css: {
                    ...data[0].svgs.legend.wrapperGroup.config.css,
                    transform: `translate(${dotRadius}px, ${transform[1] + 80}px)`,
                    transition: `all ${transitionSpeed}ms`,
                },
            };

            data[0].svgs.legend.wrapperGroup.svg.css(
                data[0].svgs.legend.wrapperGroup.config.css as Highcharts.CSSObject
            );
        }

        dataRef.current = data;
        handleChartSize(chart);

        checkExpandedItems();
    };

    const handleUpdateCarousel = (label: string, parentLabel: string) => {
        if (carouselDataTitles.map((e) => e.name).includes(parentLabel.toLowerCase() as TBehaviourLabels)) {
            const slides = carouselData.filter((el) => el.parent.toLowerCase() === parentLabel.toLowerCase());
            paceCurrentSlide.current = slides.findIndex(({ title }) =>
                label.toLowerCase().replace("-", " ").includes(title.toLowerCase().replace("-", " "))
            );
            setFilterBy(parentLabel.toLowerCase() as TBehaviourLabels);
        } else {
            const slides = carouselData.filter(
                (el) => !carouselDataTitles.map((e) => e.name).includes(el.parent.toLowerCase() as TBehaviourLabels)
            );
            triadCurrentSlide.current = slides.findIndex(({ title }) =>
                label.toLowerCase().replace("-", " ").includes(title.toLowerCase().replace("-", " "))
            );
        }

        setRender((val) => !val);
    };

    const handleExpandAll = (isChecked: boolean) => {
        if (isEmpty(data) && !isEmpty(dataRef.current)) {
            data = dataRef.current as convertedDataBB[];
        }
        data.filter((d) => !isEmpty(d.svgs.button.svg)).forEach((el, i) => {
            if (isChecked) {
                !el.expanded &&
                    handleExpandCollapse(
                        i,
                        (chartRef.current as IRefObjectForHighchartsReact).chart as Highcharts.Chart,
                        0
                    );
            } else {
                el.expanded &&
                    handleExpandCollapse(
                        i,
                        (chartRef.current as IRefObjectForHighchartsReact).chart as Highcharts.Chart,
                        0
                    );
            }
        });

        if (!isEmpty(data)) {
            dataRef.current = data;
        }

        setShouldExpandAll(isChecked);
    };

    const handleCustomLegendClick = (element: convertedDataBB["data"][number]) => {
        const elementIndex = dataRef.current?.[0].data.findIndex((item) => {
            if (element.label === "Hub Average") {
                return item.userId === element.userId;
            }
            return item.id === element.id;
        });

        if (elementIndex !== undefined) {
            const serie = chartRef.current?.chart.series[elementIndex];
            if (serie) {
                handleLegendItemClick(serie);
                handleDrawChart(chartRef.current.chart, false, true, customiseLabels);
            }
        }
    };

    const handleExpandLegend = (el: convertedDataBB, dataIdx: number) => {
        if (!dataRef.current) return;

        const itemData = dataRef.current[0].data[dataIdx];
        const legendItems = dataRef.current[0].svgs.legend.items;
        const parentItem = dataRef.current[0].svgs.legend.items[dataIdx];

        // Toggle data for expand/collapse
        itemData.isLegendExpanded = !itemData.isLegendExpanded;

        const { expandedLegendItems, rowsConfig, rows, sumOfHeights } = expandedLegendUtils(data[0]);

        if (!parentItem.mask?.config || !parentItem.mask?.svg) return;

        const maskHeight = itemData.isLegendExpanded ? expandedLegendItems[dataIdx].height : 0;

        // Animate mask expand/collapse
        parentItem.mask.config = {
            ...parentItem.mask.config,
            attr: { height: maskHeight },
        };

        parentItem.mask.svg.css({
            height: maskHeight,
            transition: "height 250ms",
        });

        // Animate parent's circle fill color
        parentItem.circle.config = {
            ...parentItem.circle.config,
            css: {
                ...parentItem.circle.config.css,
                fill: itemData.isLegendExpanded ? `${itemData.color}50` : "#fff",
            },
        };

        parentItem.circle.svg.css({
            ...parentItem.circle.config.css,
            transition: "fill 250ms",
        });

        // Update all elements translateY position based on rowsConfig
        legendItems.forEach((item, i) => {
            const { translateY } = rowsConfig[item.row];
            // Update translateY for the following props (which has the svgs)
            (["dot", "label", "circle", "count", "mask"] as const).forEach((prop) => {
                if (!isEmpty(item[prop])) {
                    if (item[prop]?.svg && !isEmpty(item[prop]?.svg)) {
                        item[prop]?.svg?.animate({ translateY }, { duration: 250 });
                    }

                    const maskItem = data[0].svgs.legend.items[i].mask;

                    if (prop !== "mask") {
                        data[0].svgs.legend.items[i][prop].config = {
                            ...data[0].svgs.legend.items[i][prop].config,
                            translate: [0, translateY],
                        };
                    }
                    if (maskItem !== undefined && maskItem.config) {
                        maskItem.config = {
                            ...data[0].svgs.legend.items[i].mask?.config,
                            translate: [0, translateY],
                        };
                    }
                }
            });

            (["dot", "label"] as const).forEach((prop, idx) => {
                item.subItems?.forEach((subItem, i) => {
                    if (!isEmpty(item[prop])) {
                        subItem[prop].svg.animate({ translateY }, { duration: 250 });

                        const item = data[0].svgs.legend.items[i]?.subItems?.[idx]?.[prop];

                        if (item?.config !== undefined)
                            item.config = {
                                ...item.config,
                                translate: [0, translateY],
                            };
                    }
                });
            });
        });

        if (chartRef.current) {
            const chart = chartRef.current.chart;

            // Because we lose data on each render, use dataRef instead
            const chartExpandableBoxes =
                dataRef.current.reduce((acc, item) => {
                    const subDataLength = item.subData.data.length;
                    acc +=
                        rowHeight +
                        (subDataLength ? buttonHeight : 0) +
                        (item.expanded ? (item.subData.data.length - 1) * rowHeight : 0) +
                        gapBetweenBehaviours;
                    return acc;
                }, 0) + legendSpace;

            const legendHeight = rows.length * legenItemVerticalSpace + 20 + legendSpace + sumOfHeights + 10;
            chart.setSize(null, chartExpandableBoxes + legendHeight, true);
        }
    };

    const handleLegendItemClick = useCallback((serie: Highcharts.Series) => {
        if (!dataRef.current) {
            return;
        }

        const index = serie.index;

        dataRef.current.forEach((el, i) => {
            if (!dataRef.current) {
                return;
            }

            el.data[index].visible = !el.data[index].visible;

            const isVisible = el.data[index].visible;
            const isEnabled = el.data[index].enabled;
            const isExpanded = dataRef.current[i].expanded;
            const isHubAverage = el.data[index]?.label === "Hub Average";

            const isNotExpandedAndVisible = !isExpanded && isVisible;

            const baseColor = isHubAverage ? hubAverageColor : el.data[index].color;
            const fillColor = isNotExpandedAndVisible ? baseColor : "transparent";
            const visibility = isVisible ? 1 : 0;

            // Summary Dots
            el.svgs.dots[index].config = {
                ...el.svgs.dots[index].config,
                css: {
                    ...el.svgs.dots[index].config.css,
                    opacity: !isEnabled ? 0 : visibility,
                    fill: !isEnabled ? "transparent" : fillColor,
                    transition: `all ${transitionSpeed / 2}ms`,
                },
            };
            el.svgs.dots[index].svg.css(el.svgs.dots[index].config.css as Highcharts.CSSObject);

            // Summary Dot line connector
            const strokeWidthLines = isNotExpandedAndVisible ? 1 : 0;

            if (el.svgs.lines[index]) {
                el.svgs.lines[index].config = {
                    ...el.svgs.lines[index].config,
                    css: {
                        ...el.svgs.lines[index].config?.css,
                        opacity: !isEnabled ? 0 : visibility,
                        "stroke-width": !isEnabled ? 0 : strokeWidthLines,
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                el.svgs.lines[index].svg.css(el.svgs.lines[index].config.css as Highcharts.CSSObject);
            }

            // Summary Dot labels
            if (i === 0 || i === 4) {
                el.svgs.dotsLabels[index].config = {
                    ...el.svgs.dotsLabels[index].config,
                    css: {
                        ...el.svgs.dotsLabels[index].config.css,
                        opacity: !isEnabled ? 0 : visibility,
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                el.svgs.dotsLabels[index].svg.css(el.svgs.dotsLabels[index].config.css as Highcharts.CSSObject);
            }

            // Subdata Dot
            el.svgs.subData.dots.forEach((subEl, i) => {
                subEl[index].config = {
                    ...subEl[index].config,
                    css: {
                        ...subEl[index].config.css,
                        opacity: !isEnabled ? 0 : visibility,
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                subEl[index].svg.css(subEl[index].config.css as Highcharts.CSSObject);
            });

            // Subdata Dot label
            if (!isEmpty(el.svgs.subData.dotsLabels[index])) {
                el.svgs.subData.dotsLabels[index].config = {
                    ...el.svgs.subData.dotsLabels[index].config,
                    css: {
                        ...el.svgs.subData.dotsLabels[index].config.css,
                        opacity: !isEnabled ? 0 : visibility,
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                el.svgs.subData.dotsLabels[index].svg.css(
                    el.svgs.subData.dotsLabels[index].config.css as Highcharts.CSSObject
                );
            }

            // Subdata Dot line connector
            const strokeWidthSublines = isVisible && isExpanded ? 1 : 0;

            el.svgs.subData.lines.forEach((subEl, i) => {
                subEl[index].config = {
                    ...subEl[index].config,
                    attr: {
                        "stroke-width": !isEnabled ? 0 : strokeWidthSublines,
                    },
                    css: {
                        ...subEl[index].config.css,
                        transition: `all ${transitionSpeed / 2}ms`,
                    },
                };
                subEl[index].svg.css(subEl[index].config.css as Highcharts.CSSObject);
            });
        });
    }, []);

    const handleChartSize = useCallback((chart: Highcharts.Chart | undefined) => {
        if (!chart) return;
        // Because we lose data on each render, use dataRef instead
        const expandedHeight =
            (dataRef?.current?.reduce((acc, item) => {
                const subDataLength = item.subData.data.length;
                acc +=
                    rowHeight +
                    (subDataLength ? buttonHeight : 0) +
                    (item.expanded ? (item.subData.data.length - 1) * rowHeight : 0) +
                    gapBetweenBehaviours;
                return acc;
            }, 0) || 0) + legendSpace;

        let legendHeight = 0;
        const customLegendWrapper = dataRef?.current?.[0].svgs.legend.wrapperGroup.svg;

        if (!isEmpty(customLegendWrapper) && dataRef.current) {
            const { rows, sumOfHeights } = expandedLegendUtils(dataRef.current[0]);
            legendHeight = rows.length * legenItemVerticalSpace + 20 + legendSpace + sumOfHeights + 10;
        } else {
            legendHeight = (chart.legend.box?.attr("height") as number) || 0;
        }

        chart.setSize(null, expandedHeight + legendHeight, true);
    }, []);

    const handleDrawChart = useCallback(
        (
            chart: Highcharts.Chart,
            isExporting: boolean,
            isRedrawing: boolean,
            customiseLabels: TCustomiseLabelsOptions,
            newChartData?: LD12Chart
        ) => {
            const noData = isEmpty(data) && isEmpty(dataRef.current);
            const status: ChartStatus = {
                isFirstRender: !isExporting && !isRedrawing,
                isRedrawing,
                isExporting,
            };

            if (isRedrawing && noData) {
                return;
            }

            // Convert chartData on load, then on every redraw we will use dataRef.current
            // Since the change is manage by Highcharts we don't need to update a state to show the changes on screen.
            if (status.isFirstRender) {
                data = convertData(newChartData || chartData, dataRef.current);
                dataRef.current = undefined;
            }

            // We lose data on every render, so use the copy we had in dataRef
            if ((isEmpty(data) && dataRef.current) || (dataRef.current && !isEqual(data, dataRef.current))) {
                data = dataRef.current;
            }

            const maximumWidth = chart.plotWidth - chartVerticalSpace * 2;
            const chartMaxWidth = maximumWidth;
            const r = chart.renderer;
            const dataEntries = Object.entries(data);

            const previousGroup: Highcharts.SVGElement | undefined = undefined;
            let barElement: ReturnType<typeof getElementBBox> | undefined = undefined;

            // Create Masks
            createMasks(dataEntries, data, status, chart, r);

            // Create Groups
            createWrapperGroups(dataEntries, data, status, r, previousGroup);

            // Create Bars, Dots and Subdata
            dataEntries.forEach((_, i) => {
                if (status.isExporting) {
                    data[i].svgs.group.svgExport.clip(data[i].svgs.mask.svgExport);
                } else {
                    if (!isEmpty(data[i].svgs.group.svg)) {
                        data[i].svgs.group.svg.clip(data[i].svgs.mask.svg);
                    }
                }

                // BACKGROUND PER SUB-BEHAVIOUR
                createSubBehaviourBackground(data[i], status, r, chart, chartMaxWidth, hideExpandButtons);

                // BAR
                createBehaviourBar(data[i], status, r, chart, chartMaxWidth);
                barElement = isExporting
                    ? getElementBBox(data[i].svgs.bar.svgExport)
                    : getElementBBox(data[i].svgs.bar.svg);

                // BUTTON
                if (!hideExpandButtons) {
                    if (data[i].subData?.data.length) {
                        createExpanderButton(data[i], status, r, chart, chartMaxWidth, showDefinitions, () =>
                            handleExpandCollapse(i, chart, transitionSpeed)
                        );
                    }
                }

                // LINES
                createPlotLines(
                    data[i],
                    status,
                    r,
                    chart,
                    chartMaxWidth,
                    status.isExporting ? data[i].svgs.group.svgExport : data[i].svgs.group.svg,
                    hideExpandButtons
                );

                // LABELS
                createLabels(data[i], status, r, chart, chartMaxWidth, showDefinitions, barElement, (label, parent) =>
                    handleUpdateCarousel(label, parent)
                );

                // SUB-LABELS
                createSubLabels(data[i], status, r, chartMaxWidth, showDefinitions, (label, parent) =>
                    handleUpdateCarousel(label, parent)
                );
            });

            // ----------------------------------------------------------------
            // SERIES
            // ----------------------------------------------------------------

            // SUMMARY DOT
            createSummaryDot(dataEntries, status, r, barElement);

            // SUMMARY DOT LABELS

            createSummaryDotLabel(dataEntries, status, r, customiseLabels);
            // LINES THAT CONNECT DOTS
            createDotLineConnector(dataEntries, status, r);

            // SUB-BEHAVIOUR DOTS

            createSubBehaviourDots(dataEntries, status, r, barElement, customiseLabels);
            // LINES THAT CONNECT SUBDOTS
            createSubDotLineConnector(dataEntries, status, r);

            // LEGEND ITEMS
            if (useCustomLegend) {
                createCustomLegend(
                    dataEntries,
                    status,
                    r,
                    chart,
                    (element) => handleCustomLegendClick(element),
                    (elements, index) => handleExpandLegend(elements, index)
                );
            }

            if (status.isFirstRender) {
                prevChartData.current = chartData;
            }

            if (newChartData) {
                prevChartData.current = newChartData;
            }

            setRender((val) => !val);
            dataRef.current = data;
        },
        [showDefinitions, customiseLabels, chartData]
    );

    // Pace Definitions External value
    useEffect(() => {
        if (moreOptions?.showPaceDefinitions !== undefined) {
            setShowDefinitions(moreOptions?.showPaceDefinitions);
        }
    }, [moreOptions?.showPaceDefinitions]);

    // Adjust Legend height on first load
    useEffect(() => {
        if (!isEmpty(chartOptions) && !arechartOptionsLoaded.current) {
            arechartOptionsLoaded.current = true;
            handleChartSize(chartRef.current?.chart);
        }
    }, [chartOptions, handleChartSize]);

    // Init
    useEffect(() => {
        // Render chart for the 1st time + keep updating export with last value of customiseLabels
        if (!isEmpty(chartData)) {
            const options = getOptions({
                chartData,
                handleDrawChart,
                handleLegendItemClick,
                hideLegend,
                shouldShowExportingMenu,
                customiseLabels,
            });
            setChartOptions(options);
        }

        // Update chart when chartData changes
        if (!chartRef.current?.chart) return;

        if (prevChartData.current && !isEqual(chartData, prevChartData.current)) {
            // Put all SVGs in 1 simple array
            const items = dataRef.current?.map((item) => Object.values(item.svgs));
            const allSvgSetups = items?.flatMap((item) =>
                item.flatMap((it, i, { length }) => {
                    if (length - 1 === i) {
                        return flattenDeep(Object.values(it));
                    } else {
                        return it;
                    }
                })
            );

            // Destroy all SVGs
            allSvgSetups?.forEach((setup) => {
                if (setup?.svg && !isEmpty(setup.svg)) setup.svg.destroy();
            });

            if (useCustomLegend) {
                dataRef.current?.[0].svgs.legend.key.svg.destroy();
                dataRef.current?.[0].svgs.legend.items
                    .flatMap((item) => Object.values(item))
                    .forEach((item: any) => {
                        if (item?.svg && !isEmpty(item.svg)) item.svg.destroy();
                    });
            }
            // Keep old data visibility value. The parent will always send visible true
            // So we need to make sure to keep all invisible data as such
            const newChartData = {
                ...chartData,
                series_labels: chartData.series_labels.map((label) => {
                    const existingData = dataRef.current?.[0]?.data.find((d) => d.id === label.entity_id);

                    return {
                        ...label,
                        visible: existingData?.visible ?? true,
                        isLegendExpanded: existingData?.isLegendExpanded,
                    };
                }),
            };

            // Draw the chart again using the new Data
            handleDrawChart(chartRef.current.chart, false, false, customiseLabels, newChartData);

            const options = getOptions({
                chartData: newChartData,
                handleDrawChart,
                handleLegendItemClick,
                hideLegend,
                shouldShowExportingMenu,
                customiseLabels,
            });
            setChartOptions(options);

            handleChartSize(chartRef.current.chart);
        }

        handleDrawChart(chartRef.current.chart, false, true, customiseLabels);
    }, [
        chartData,
        shouldShowExportingMenu,
        hideLegend,
        useCustomLegend,
        customiseLabels,
        handleDrawChart,
        handleLegendItemClick,
        handleChartSize,
    ]);

    return {
        chartOptions,
        chartData,
        chartRef,
        shouldExpandAll,
        hideExpandButtons,
        showDefinitions,
        paceCurrentSlide,
        triadCurrentSlide,
        filterBy,
        customiseLabels,
        setFilterBy,
        handleExpandAll,
        handleCheckExpandAll,
        handleCheckPaceDefinitions,
        handleSetCustomiseLabels,
    };
};

export default useBehaviouralBreakdown;
